import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const getFocusableElements = (container) => {
  if (!container) return [];
  const nodes = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
  return nodes.filter((node) => {
    if (!(node instanceof HTMLElement)) return false;
    if (node.hasAttribute("hidden")) return false;
    if (node.getAttribute("aria-hidden") === "true") return false;
    if (node.tabIndex < 0) return false;
    const style = window.getComputedStyle(node);
    return style.display !== "none" && style.visibility !== "hidden";
  });
};

export default function useFocusTrap({
  isOpen,
  containerRef,
  onClose,
  initialFocusRef,
  returnFocusRef,
  closeOnEscape = true,
  lockScroll = false,
}) {
  const fallbackReturnRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const container = containerRef?.current;
    if (!container) return undefined;

    const activeElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    fallbackReturnRef.current = activeElement;
    if (returnFocusRef && !returnFocusRef.current) {
      returnFocusRef.current = activeElement;
    }

    if (container.tabIndex < 0) {
      container.setAttribute("tabindex", "-1");
    }

    const focusFirst = () => {
      const focusable = getFocusableElements(container);
      const preferred =
        initialFocusRef?.current ||
        focusable[0] ||
        container;
      preferred.focus();
    };
    const rafId = window.requestAnimationFrame(focusFirst);

    const previousOverflow = document.body.style.overflow;
    if (lockScroll) {
      document.body.style.overflow = "hidden";
    }

    const onKeyDown = (event) => {
      if (closeOnEscape && event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = getFocusableElements(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;

      if (event.shiftKey) {
        if (current === first || current === container) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener("keydown", onKeyDown);

    return () => {
      window.cancelAnimationFrame(rafId);
      container.removeEventListener("keydown", onKeyDown);
      if (lockScroll) {
        document.body.style.overflow = previousOverflow;
      }

      const returnTo = returnFocusRef?.current || fallbackReturnRef.current;
      if (returnTo && document.contains(returnTo)) {
        window.requestAnimationFrame(() => returnTo.focus());
      }
      if (returnFocusRef) {
        returnFocusRef.current = null;
      }
      fallbackReturnRef.current = null;
    };
  }, [
    closeOnEscape,
    containerRef,
    initialFocusRef,
    isOpen,
    lockScroll,
    returnFocusRef,
  ]);
}
